import { SectionsHeroParallax, SectionProps } from "@/types";
import { heroParallaxVariants, HeroParallaxVariantProps } from "./hero-parallax.variants";

export type HeroParallaxImage = {
  id: string;
  src: string;
  depth: number;
  className?: string;
  wrapperClassName?: string;
  width: number;
  height: number;
};

export type UseHeroParallaxProps = SectionProps<
  SectionsHeroParallax,
  HeroParallaxVariantProps
>;

export const useHeroParallax = (props: UseHeroParallaxProps) => {
  const { className = "", sectionName, data } = props;
  const imagesBase = data.images;
  const styles = heroParallaxVariants();

  const images: HeroParallaxImage[] = [
    {
      id: "1",
      depth: 0.5,
      src: imagesBase[0]?.url as string,
      wrapperClassName:
        "top-[4%] left-[-3%] md:top-[-6%] md:left-[-3%] lg:top-[-1%] lg:left-[-1%]",
      className: "w-18 h-18 lg:w-42 lg:h-42 2xl:w-56 2xl:h-56 object-cover",
      width: imagesBase[0]?.width as number,
      height: imagesBase[0]?.height as number,
    },
    {
      id: "2",
      depth: 1,
      src: imagesBase[1]?.url as string,
      wrapperClassName:
        "top-[12%] left-[28%] lg:top-[12%] lg:left-[20%] xl:top-[7%] xl:left-[20%] border border-border-neutral-default",
      className: "w-16 h-16 md:w-31 md:h-31 object-cover",
      width: imagesBase[1]?.width as number,
      height: imagesBase[1]?.height as number,
    },
    {
      id: "3",
      depth: 2,
      src: imagesBase[2]?.url as string,
      wrapperClassName:
        "top-[-6%] left-[26%] lg:top-[-2%] lg:left-[38%] xl:top-[-7%] xl:left-[40%] ",
      className: "w-26 h-25 md:w-61 md:h-60 2xl:w-69 2xl:h-68 object-cover",
      width: imagesBase[2]?.width as number,
      height: imagesBase[2]?.height as number,
    },
    {
      id: "4",
      depth: 0.5,
      src: imagesBase[3]?.url as string,
      wrapperClassName:
        "top-[-1%] left-auto right-[-6%] lg:top-[-2%] lg:right-[3%] xl:top-[-5%] xl:right-[3%]",
      className: "w-40 h-21 lg:w-74 lg:h-55 2xl:w-98 2xl:h-79 object-cover",
      width: imagesBase[3]?.width as number,
      height: imagesBase[3]?.height as number,
    },
    {
      id: "5",
      depth: -1.5,
      src: imagesBase[4]?.url as string,
      wrapperClassName:
        "top-[19%] left-[-8%] lg:top-[18%] lg:left-[-8%] xl:top-[24%] xl:left-[3%]",
      className: "w-25 h-34 lg:w-60 lg:h-71 2xl:w-84 2xl:h-93 object-cover",
      width: imagesBase[4]?.width as number,
      height: imagesBase[4]?.height as number,
    },
    {
      id: "6",
      depth: 1,
      src: imagesBase[5]?.url as string,
      wrapperClassName:
        "top-[16%] left-[74%] lg:top-[22%] lg:left-[70%] xl:top-[8%] xl:left-[63%] border border-border-neutral-default",
      className: "w-16 h-16 md:w-24 md:h-24 lg:w-31 lg:h-31 object-cover",
      width: imagesBase[5]?.width as number,
      height: imagesBase[5]?.height as number,
    },
    {
      id: "7",
      depth: 1,
      src: imagesBase[6]?.url as string,
      wrapperClassName:
        "top-[32%] left-auto right-[-16%] lg:top-[30%] lg:right-[-14%] xl:top-[26%] xl:right-[-2%]",
      className: "w-28 h-25 lg:w-63 lg:h-60 2xl:w-84 2xl:h-81 object-cover",
      width: imagesBase[6]?.width as number,
      height: imagesBase[6]?.height as number,
    },
    {
      id: "8",
      depth: 0.8,
      src: imagesBase[7]?.url as string,
      wrapperClassName:
        "top-[50%] left-[-10%] lg:top-[48%] lg:left-[-6%] xl:top-[63%] xl:left-[-1%]",
      className: "w-26 h-32 lg:w-66 lg:h-72 2xl:w-86 2xl:h-92 object-cover",
      width: imagesBase[7]?.width as number,
      height: imagesBase[7]?.height as number,
    },
    {
      id: "9",
      depth: -2,
      src: imagesBase[8]?.url as string,
      wrapperClassName:
        "top-[68%] left-[8%] lg:top-[62%] lg:left-[25%] xl:top-[52%] xl:left-[22%]",
      className: "w-28 h-26 lg:w-70 lg:h-56 2xl:w-76 2xl:h-62 object-cover",
      width: imagesBase[8]?.width as number,
      height: imagesBase[8]?.height as number,
    },
    {
      id: "10",
      depth: -0.8,
      src: imagesBase[9]?.url as string,
      wrapperClassName:
        "top-[62%] left-[45%] md:left-[60%] lg:top-[55%] xl:top-[48%] xl:left-[64%]",
      className: "w-35 h-50 lg:w-64 lg:h-88 2xl:w-69 2xl:h-99 object-cover",
      width: imagesBase[9]?.width as number,
      height: imagesBase[9]?.height as number,
    },
    {
      id: "11",
      depth: 1,
      src: imagesBase[10]?.url as string,
      wrapperClassName:
        "top-[54%] left-auto right-[-18%] lg:top-[52%] lg:right-[-14%] xl:top-[58%] xl:right-[-5%]",
      className: "w-26 h-26 lg:w-62 lg:h-59 2xl:w-72 2xl:h-69 object-cover",
      width: imagesBase[10]?.width as number,
      height: imagesBase[10]?.height as number,
    },
  ];

  return {
    className,
    styles,
    sectionName,
    data,
    images,
  };
};
